// Thermal receipt / kitchen ticket printing.
//
// Restaurant tickets show the restaurant-side amount only: original item prices,
// no Livresto discount/remise/coupon, and no delivery fee. Client receipts show
// the client-side amount with discounts, delivery, and service fees.

function esc(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function money(value) {
  return Number(value || 0).toFixed(3);
}

function numberOrNull(value) {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function firstAmount(...values) {
  for (const value of values) {
    const number = numberOrNull(value);
    if (number !== null) return number;
  }
  return 0;
}

// Livresto Express brand mark - inline SVG of the "LE" location-pin, drawn in
// solid black so it prints crisply on thermal/receipt printers.
const LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96" role="img" aria-label="Black and white icon">
  <g fill="#000000" fill-rule="nonzero">
  <path d="M 54 25.07 L 53 25.48 L 52.4 25 L 48.61 20 L 48.26 19 L 49.27 18 L 54 14.59 L 55 14.57 L 59 15.87 L 62.65 18 L 62 19.13 Z"/>
  <path d="M 45 54.03 L 44 54.56 L 42.97 54 L 22.99 27 L 24.94 24 L 29 19.93 L 36 15.72 L 37.19 16 L 34.71 18 L 34.99 20 L 54.45 46 L 54.33 47 Z"/>
  <path d="M 65 39.03 L 64 39.59 L 63 39.27 L 58.48 33 L 68.56 25 L 70 24.67 L 72.3 28 L 73.99 32 Z"/>
  <path d="M 48 81.23 L 46 81.09 L 44.56 80 L 23.97 59 L 21 53.77 L 19.36 49 L 18.8 46 L 19 40.65 L 40 67.94 L 41 68.24 L 75 42.8 L 75.6 43 L 75.81 45 L 75.17 49 L 74 52.6 L 71 58.31 L 68.06 62 L 51 79.06 Z"/>
  </g>
</svg>
`;

function fmtDateTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("fr-FR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function paymentLabel(order) {
  if (order.payementMode && order.payementMode.name) return order.payementMode.name;
  const modes = { 1: "Especes", 2: "Carte bancaire", 3: "En ligne", 4: "Portefeuille" };
  return modes[order.payement_mode_id] || "Non specifie";
}

function getProductQuantity(product) {
  return Number(product.quantity ?? product.pivot?.quantity ?? 1) || 1;
}

function getProductName(product) {
  return product.name || product.product?.name || "Produit";
}

function normalizeReceiptItems(product) {
  const raw = Array.isArray(product.items) && product.items.length
    ? product.items
    : (product.pivot && product.pivot.items) || [];

  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object" || typeof raw === "number") return [raw];
  if (typeof raw !== "string") return [];

  const trimmed = raw.trim();
  if (!trimmed) return [];

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed;
  } catch {
    // Legacy orders often store option ids as CSV.
  }

  return trimmed
    .replace(/^[\s[\]"']+|[\s[\]"']+$/g, "")
    .split(",")
    .map((item) => item.trim().replace(/^["']+|["']+$/g, ""))
    .filter(Boolean);
}

function optionItems(product) {
  const options = product.options || product.product?.options || [];
  return options.flatMap((option) =>
    (option.items || []).map((item) => ({ option, item }))
  );
}

function itemId(item) {
  if (item && typeof item === "object") return item.id != null ? String(item.id) : "";
  return String(item ?? "");
}

function findOptionItem(product, item) {
  const id = itemId(item);
  const optionId = item && typeof item === "object" && item.option_id != null
    ? String(item.option_id)
    : null;

  return optionItems(product).find(({ option, item: optionItem }) => {
    const sameItem = optionItem && optionItem.id != null && String(optionItem.id) === id;
    const sameOption = optionId && option && option.id != null && String(option.id) === optionId;
    return sameItem || (sameOption && sameItem);
  });
}

function itemLabel(product, item) {
  if (item && typeof item === "object") return item.name || item.label || item.title || "";
  const match = findOptionItem(product, item);
  return match?.item?.name || String(item || "");
}

function itemPrice(product, item) {
  if (item && typeof item === "object") return Number(item.price || 0);
  const match = findOptionItem(product, item);
  return Number(match?.item?.price || 0);
}

function isSizeItem(product, item) {
  const match = findOptionItem(product, item);
  if (match?.option) return Number(match.option.is_size) === 1;

  const optionId = item && typeof item === "object" && item.option_id != null
    ? String(item.option_id)
    : null;
  if (!optionId) return false;

  const options = product.options || product.product?.options || [];
  return options.some((option) =>
    option && option.id != null && String(option.id) === optionId && Number(option.is_size) === 1
  );
}

function originalLineTotal(product, qty, items) {
  const basePrice = firstAmount(product.price, product.base_price, product.product?.price);
  let hasSize = false;
  const optionsTotal = items.reduce((sum, item) => {
    if (isSizeItem(product, item)) hasSize = true;
    return sum + itemPrice(product, item);
  }, 0);

  return ((hasSize ? 0 : basePrice) + optionsTotal) * qty;
}

function clientLineTotal(product, qty) {
  return firstAmount(
    product.total_price,
    product.pivot?.total,
    product.subtotal,
    product.totalPrice,
    product.total,
    firstAmount(product.price, product.base_price, product.product?.price) * qty
  );
}

function buildProducts(order, type) {
  return (order.products || []).map((product) => {
    const qty = getProductQuantity(product);
    const items = normalizeReceiptItems(product);
    const lineTotal = type === "restaurant"
      ? originalLineTotal(product, qty, items)
      : clientLineTotal(product, qty);
    const options = items.map((item) => itemLabel(product, item)).filter(Boolean);
    const note = (product.pivot && product.pivot.specifications) || product.specifications || "";

    return {
      qty,
      name: getProductName(product),
      options,
      note,
      lineTotal,
    };
  });
}

function customerName(order) {
  const user = order.user || order.users;
  if (!user) return "";
  if (user.first_name) return `${user.first_name} ${user.last_name || ""}`.trim();
  return user.name || "";
}

function addressLabel(order) {
  const address = order.address;
  if (!address) return "";
  return address.address || address.street || address.label || "";
}

function buildTotals(order, products, type) {
  const computedItemsTotal = products.reduce((sum, product) => sum + Number(product.lineTotal || 0), 0);

  if (type === "restaurant") {
    const total = firstAmount(
      order.orderCost_restaurant,
      numberOrNull(order.total_restaurant) !== null
        ? Number(order.total_restaurant) - Number(order.deliveryCost || 0)
        : null,
      order.orderCost,
      computedItemsTotal
    );

    return {
      subtotal: total,
      total,
      rows: [
        { label: "Sous-total", value: total },
      ],
    };
  }

  const delivery = Number(order.deliveryCost || 0);
  const serviceFee = Number(order.service_fee || 0);
  const remise = Number(order.remise || order.discount || 0);
  const pointsDiscount = Number(order.points_discount || 0);
  const subtotalAfterDiscount = firstAmount(order.orderCost_client, order.orderCost, computedItemsTotal - remise - pointsDiscount);
  const total = firstAmount(order.total_client, order.total_price, order.total, subtotalAfterDiscount + delivery + serviceFee);

  const rows = [
    { label: "Articles", value: computedItemsTotal },
  ];
  if (remise > 0) rows.push({ label: "Remise Livresto", value: -remise });
  if (pointsDiscount > 0) rows.push({ label: "Points fidelite", value: -pointsDiscount });
  if (remise > 0 || pointsDiscount > 0) rows.push({ label: "Sous-total", value: subtotalAfterDiscount });
  if (delivery > 0) rows.push({ label: "Livraison", value: delivery });
  if (delivery === 0 && order.free_delivery && order.free_delivery_original_cost > 0) {
    rows.push({ label: "Livraison offerte", value: 0 });
  }
  if (serviceFee > 0) rows.push({ label: "Frais service", value: serviceFee });

  return {
    subtotal: subtotalAfterDiscount,
    total,
    rows,
  };
}

function totalsHtml(rows) {
  return rows.map((row) => `
    <div class="row"><span>${esc(row.label)}</span><span>${money(row.value)}</span></div>`
  ).join("");
}

function buildTicketHtml(order, restaurant, type = "restaurant") {
  const restaurantName = (restaurant && restaurant.name) || order.restaurant?.name || "Restaurant";
  const ref = order.ref || order.reference || order.id || "Reference indisponible";
  const isDelivery = order.orderType !== "takeaway" && Number(order.order_mode || 1) !== 2;
  const products = buildProducts(order, type);
  const totals = buildTotals(order, products, type);
  const payment = paymentLabel(order);
  const clientName = customerName(order);
  const clientAddress = addressLabel(order);
  const title = type === "client" ? "Ticket client" : "Ticket restaurant";

  const itemsHtml = products.map((product) => `
    <div class="item">
      <div class="row">
        <span class="bold">${product.qty} x ${esc(product.name)}</span>
        <span>${money(product.lineTotal)}</span>
      </div>
      ${product.options.map((option) => `<div class="sub">+ ${esc(option)}</div>`).join("")}
      ${product.note ? `<div class="note">** ${esc(product.note)}</div>` : ""}
    </div>`).join("");

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${esc(title)} ${esc(ref)}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #000; background: #fff; width: 80mm; padding: 4mm 3mm; font-size: 13px; line-height: 1.5; -webkit-font-smoothing: antialiased; }
  .logo { text-align: center; margin-bottom: 2px; }
  .logo svg { width: 58px; height: 58px; }
  .brand { text-align: center; font-size: 18px; font-weight: 800; letter-spacing: 2px; }
  .subbrand { text-align: center; font-size: 13px; margin-top: 2px; }
  .ticket-type { text-align: center; font-size: 12px; letter-spacing: 1px; margin-top: 3px; font-weight: 700; text-transform: uppercase; }
  .hr { border-top: 1px dashed #000; margin: 8px 0; }
  .row { display: flex; justify-content: space-between; gap: 8px; }
  .row span:first-child { min-width: 0; }
  .row span:last-child { flex-shrink: 0; }
  .ref-block { text-align: center; margin: 4px 0 2px; }
  .ref { font-size: 30px; font-weight: 800; letter-spacing: 1px; line-height: 1.1; }
  .badge { display: inline-block; margin-top: 6px; padding: 2px 12px; border: 2px solid #000; border-radius: 6px; font-size: 13px; font-weight: 700; letter-spacing: 1px; }
  .muted { font-size: 12px; }
  .center { text-align: center; }
  .section-title { font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
  .item { margin: 7px 0; }
  .item .row span:first-child { font-size: 14px; }
  .sub { padding-left: 16px; font-size: 12px; }
  .note { padding-left: 16px; font-size: 12px; font-style: italic; }
  .tot { font-size: 18px; font-weight: 800; margin-top: 2px; }
  .bold { font-weight: 700; }
  @media print { @page { size: 80mm auto; margin: 0; } body { width: 80mm; } }
</style>
</head>
<body>
  <div class="logo">${LOGO_SVG}</div>
  <div class="brand">LIVRESTO EXPRESS</div>
  <div class="subbrand">${esc(restaurantName)}</div>
  <div class="ticket-type">${esc(title)}</div>
  <div class="hr"></div>

  <div class="ref-block">
    <div class="ref">#${esc(ref)}</div>
    <div class="badge">${isDelivery ? "LIVRAISON" : "A EMPORTER"}</div>
  </div>
  <div class="muted center">${esc(fmtDateTime(order.created_at || order.orderDate))}</div>

  ${type === "client" && (clientName || clientAddress) ? `
  <div class="hr"></div>
  <div class="section-title">Client</div>
  ${clientName ? `<div class="row"><span>Nom</span><span>${esc(clientName)}</span></div>` : ""}
  ${clientAddress ? `<div class="muted">${esc(clientAddress)}</div>` : ""}
  ` : ""}

  <div class="hr"></div>
  <div class="section-title">Articles</div>
  ${itemsHtml || '<div class="center muted">Aucun produit</div>'}
  <div class="hr"></div>

  ${totalsHtml(totals.rows)}
  <div class="row tot"><span>TOTAL</span><span>${money(totals.total)} TND</span></div>
  <div class="muted" style="margin-top:4px;">Paiement: <span class="bold">${esc(payment)}</span></div>

  <div class="hr"></div>
  <div class="center">Merci !</div>
  <div class="center muted">LIVRESTO EXPRESS</div>

  <script>
    window.onload = function () { window.focus(); window.print(); };
    window.onafterprint = function () { window.close(); };
  </script>
</body>
</html>`;
}

function openTicket(order, restaurant, type) {
  if (!order) return;
  const html = buildTicketHtml(order, restaurant, type);
  const win = window.open("", "_blank", "width=380,height=640");
  if (!win) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
}

export function printOrderTicket(order, restaurant) {
  openTicket(order, restaurant, "restaurant");
}

export function printRestaurantOrderTicket(order, restaurant) {
  openTicket(order, restaurant, "restaurant");
}

export function printClientOrderTicket(order, restaurant) {
  openTicket(order, restaurant, "client");
}
